import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { MODAL_SIZE, ModalForm } from "components/Modal";
import { SIZE, TYPE } from "components/Form";
import { MUTATION_TYPE } from "hooks";
import { SUBMIT_AI_ROLE_BUILDER_JOB } from "graphql/mutations";

/**
 * AIRoleBuilderForm
 * Form component for AI Role Builder input
 *
 * @param {Function} onCompleted    Callback when form is completed
 * @param {Function} onError        Callback when form submission fails
 * @param {String} companyName      Pre-filled company name (optional)
 * @param {String} companyUrl       Pre-filled company URL (optional)
 * @param {String} missionId        Mission the generated role is requested for
 */
const AIRoleBuilderForm = ({ onCompleted, onError, companyName, companyUrl, missionId, ...props }) => {
  const initialValues = {
    companyName,
    companyUrl,
    missionId,
  };

  const formProps = {
    initialValues,
    mutationType: MUTATION_TYPE.add,
    mutationData: {
      mutationName: {
        add: SUBMIT_AI_ROLE_BUILDER_JOB,
      },
      refetchAfterMutate: false,
    },
  };

  return (
    <ModalForm
      size={MODAL_SIZE.large}
      title={<FormattedMessage id="AIRoleBuilder.Modal.Title" />}
      description={<FormattedMessage id="AIRoleBuilder.Form.Description" />}
      data={getFormData(props)}
      onCompleted={onCompleted}
      onError={onError}
      primaryButtonMessageId="AIRoleBuilder.Form.Submit"
      secondaryButtonMessageId="Global.Cancel"
      showSave
      showCancel
      {...formProps}
      {...props}
    />
  );
};

const getFormData = ({ hideDescriptionField = false } = {}) => [
  {
    items: [
      {
        label: <FormattedMessage id="AIRoleBuilder.Form.CompanyName.Label" />,
        type: TYPE.input,
        size: SIZE.xlarge,
        properties: {
          id: "companyName",
          name: "companyName",
          placeholder: "e.g., Acme Corp",
          required: true,
        },
      },
      {
        label: <FormattedMessage id="AIRoleBuilder.Form.CompanyURL.Label" />,
        type: TYPE.input,
        size: SIZE.xlarge,
        properties: {
          id: "companyUrl",
          name: "companyUrl",
          type: "url",
          placeholder: "e.g., https://www.acme.com",
          required: true,
        },
      },
      !hideDescriptionField && {
        label: <FormattedMessage id="AIRoleBuilder.Form.JobDescription.Label" />,
        type: TYPE.textarea,
        size: SIZE.xlarge,
        properties: {
          id: "jobDescription",
          name: "jobDescription",
          placeholder: "Paste the job description here...",
          required: false,
          rows: 10,
        },
      },
      {
        label: <FormattedMessage id="AIRoleBuilder.Form.JobDescriptionFile.Label" />,
        description: <FormattedMessage id="AIRoleBuilder.Form.JobDescriptionFile.Description" />,
        type: TYPE.fileDropzone,
        size: SIZE.xlarge,
        properties: {
          name: "jobDescriptionFile",
          accept: "application/pdf",
          maxFiles: 1,
          required: false,
          dropzoneTextId: "AIRoleBuilder.Form.JobDescriptionFile.DropzoneText",
        },
      },
    ].filter(Boolean),
  },
];

AIRoleBuilderForm.propTypes = {
  onCompleted: PropTypes.func.isRequired,
  onError: PropTypes.func,
  companyName: PropTypes.string,
  companyUrl: PropTypes.string,
  missionId: PropTypes.string,
};

export default AIRoleBuilderForm;
